#!/usr/bin/env node
/**
 * MCP Server Bootstrap - installs dependencies before starting server.
 *
 * Uses only Node.js built-ins (no external dependencies required).
 * Self-locates plugin root via import.meta.url (doesn't rely on CLAUDE_PLUGIN_ROOT).
 *
 * Dependency installation strategy:
 * 1. Fast path: node_modules already exists → skip
 * 2. Package manager: Run bun install or npm install
 *
 * IMPORTANT: MCP servers must NOT log to stderr - Claude Code treats stderr output
 * as an error and may mark the MCP server as failed. All logging goes to file.
 */
import { execSync } from 'node:child_process';
import {
  appendFileSync,
  existsSync,
  mkdirSync,
  readFileSync,
  rmSync,
  unlinkSync,
  writeFileSync,
} from 'node:fs';
import { homedir } from 'node:os';
import { dirname, join } from 'node:path';
import { fileURLToPath } from 'node:url';

// Expand shell variable references like ${PWD} that Claude Code doesn't expand
// when passing env vars from .mcp.json to the MCP server process.
function expandShellVars(value: string): string {
  return value.replace(/\$\{(\w+)\}/g, (match, name: string) => process.env[name] ?? match);
}

// Logging helper - writes to file since MCP servers must NOT use stderr
// (Claude Code treats stderr as error and may fail the server)
// JSON format matches pino output for consistency
// Use PROJECT_ROOT if available (set by Claude Code), else fall back to home dir
const rawProjectRoot = process.env['PROJECT_ROOT'];
const envProjectRoot =
  rawProjectRoot !== undefined && rawProjectRoot !== ''
    ? expandShellVars(rawProjectRoot)
    : undefined;
const logDir =
  envProjectRoot !== undefined && envProjectRoot !== ''
    ? join(envProjectRoot, '.bluera', 'bluera-knowledge', 'logs')
    : join(homedir(), '.bluera', 'bluera-knowledge', 'logs');
const logFile = join(logDir, 'app.log');

const log = (
  level: 'info' | 'error' | 'debug',
  msg: string,
  data?: Record<string, unknown>
): void => {
  try {
    mkdirSync(logDir, { recursive: true });
    const entry = {
      time: new Date().toISOString(),
      level,
      module: 'bootstrap',
      msg,
      ...data,
    };
    appendFileSync(logFile, `${JSON.stringify(entry)}\n`);
  } catch {
    // Silently fail - we cannot use stderr for MCP servers
  }
};

// Self-locate plugin root from this file's path
// dist/mcp/bootstrap.js -> plugin root (two directories up)
const __filename = fileURLToPath(import.meta.url);
const __dirname = dirname(__filename);
const pluginRoot = join(__dirname, '..', '..');

// Lock file to detect interrupted installs
const installLockFile = join(pluginRoot, '.node_modules_installing');

// Get version from package.json for logging
const getVersion = (): string => {
  try {
    const pkg: unknown = JSON.parse(readFileSync(join(pluginRoot, 'package.json'), 'utf-8'));
    if (
      typeof pkg === 'object' &&
      pkg !== null &&
      'version' in pkg &&
      typeof pkg.version === 'string'
    ) {
      return `v${pkg.version}`;
    }
    return 'unknown';
  } catch {
    return 'unknown';
  }
};

/**
 * Check if build tools are available for native module compilation.
 * Logs error to file and throws if make is not found.
 */
function checkBuildTools(): void {
  try {
    execSync('which make', { stdio: 'ignore' });
    log('debug', 'Build tools available (make found)');
  } catch {
    log('error', 'Build tools not found - make is required for native modules', {
      fix: 'Install build-essential (Debian/Ubuntu), Development Tools (Fedora), or Xcode CLI Tools (macOS)',
    });
    throw new Error(
      'Build tools not found. Install: sudo apt install build-essential (Debian/Ubuntu), ' +
        'sudo dnf groupinstall "Development Tools" (Fedora), or xcode-select --install (macOS)'
    );
  }
}

/**
 * Install dependencies using bun or npm.
 * Uses stdio: 'pipe' to capture output and avoid corrupting MCP stdio transport.
 */
function installWithPackageManager(): void {
  const hasBun = ((): boolean => {
    try {
      execSync('which bun', { stdio: 'ignore' });
      return true;
    } catch {
      return false;
    }
  })();

  // CRITICAL: npm needs peer-deps bypass flag (tree-sitter-go/rust have conflicting requirements)
  const cmd = hasBun ? 'bun install --frozen-lockfile' : 'npm install --legacy-peer-deps --silent';
  log('info', 'Installing dependencies with package manager', { hasBun, cmd });

  try {
    // Use stdio: 'pipe' to capture output - 'inherit' would corrupt MCP stdio transport
    // Skip browser downloads during install - browsers are installed separately by setup.sh
    const output = execSync(cmd, {
      cwd: pluginRoot,
      stdio: 'pipe',
      env: {
        ...process.env,
        PUPPETEER_SKIP_DOWNLOAD: '1',
        PLAYWRIGHT_SKIP_BROWSER_DOWNLOAD: '1',
      },
    });
    if (output.length > 0) {
      log('debug', 'Install output', { output: output.toString().trim() });
    }
    log('info', 'Dependencies installed via package manager');
  } catch (error) {
    // Log installation failure details before re-throwing
    // execSync throws an object with stdout/stderr buffers on failure
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorDetails: Record<string, unknown> = { message: errorMessage };

    if (typeof error === 'object' && error !== null) {
      if ('stdout' in error && Buffer.isBuffer(error.stdout)) {
        errorDetails['stdout'] = error.stdout.toString().trim();
      }
      if ('stderr' in error && Buffer.isBuffer(error.stderr)) {
        errorDetails['stderr'] = error.stderr.toString().trim();
      }
    }

    log('error', 'Dependency installation failed', errorDetails);
    throw error;
  }
}

/**
 * Ensure dependencies are available.
 * Uses a lock file to detect and recover from interrupted installs.
 */
function ensureDependencies(): void {
  const startTime = Date.now();
  log('debug', 'ensureDependencies starting');

  const nodeModulesPath = join(pluginRoot, 'node_modules');

  // Check for interrupted install - lock file exists means previous install was killed
  log('debug', 'Checking for interrupted install lock file', { lockFile: installLockFile });
  if (existsSync(installLockFile)) {
    log('info', 'Detected interrupted install, cleaning up');
    rmSync(nodeModulesPath, { recursive: true, force: true });
    unlinkSync(installLockFile);
    log('debug', 'Cleanup complete');
  }

  // Fast path: already installed
  log('debug', 'Checking node_modules exists', { path: nodeModulesPath });
  if (existsSync(nodeModulesPath)) {
    log('info', 'Dependencies already installed', { elapsedMs: Date.now() - startTime });
    return;
  }

  // Verify package.json exists before attempting install
  const packageJsonPath = join(pluginRoot, 'package.json');
  if (!existsSync(packageJsonPath)) {
    log('error', 'package.json not found in plugin root — plugin cache may be corrupt', {
      pluginRoot,
      fix: 'Reinstall: /plugin uninstall bluera-knowledge && /plugin install bluera-knowledge@bluera',
    });
    throw new Error(
      `package.json not found at ${pluginRoot}. ` +
        'Plugin cache may be corrupt. ' +
        'Fix: /plugin uninstall bluera-knowledge && /plugin install bluera-knowledge@bluera'
    );
  }

  // Check build tools before attempting install
  log('debug', 'Checking build tools before install');
  checkBuildTools();

  // Create lock file before install (left behind if install interrupted/fails)
  log('debug', 'Creating lock file before install');
  writeFileSync(installLockFile, new Date().toISOString());

  const installStart = Date.now();
  log('debug', 'Starting package manager install');
  installWithPackageManager();
  log('debug', 'Package manager install finished', { elapsedMs: Date.now() - installStart });

  // Remove lock file on success
  unlinkSync(installLockFile);
  log('debug', 'ensureDependencies complete', { totalElapsedMs: Date.now() - startTime });
}

// Main entry point
const bootstrapStartTime = Date.now();
const VERSION = getVersion();
log('info', 'Bootstrap starting', { pluginRoot, version: VERSION });

log('debug', 'Calling ensureDependencies');
ensureDependencies();
log('debug', 'ensureDependencies returned', { elapsedMs: Date.now() - bootstrapStartTime });

// Now that dependencies are installed, import and run the server
// Dynamic import required because @modelcontextprotocol/sdk wouldn't be available before install
log('info', 'Loading server module');
const importStart = Date.now();
const { runMCPServer } = await import('./server.js');
log('debug', 'Server module loaded', { elapsedMs: Date.now() - importStart });

const projectRoot =
  process.env['PROJECT_ROOT'] !== undefined
    ? expandShellVars(process.env['PROJECT_ROOT'])
    : undefined;
if (projectRoot === undefined) {
  throw new Error('PROJECT_ROOT environment variable is required');
}

log('info', 'Starting MCP server', {
  projectRoot,
  dataDir: process.env['DATA_DIR'],
});

await runMCPServer({
  dataDir: process.env['DATA_DIR'],
  config: process.env['CONFIG_PATH'],
  projectRoot,
});
